import React from 'react';

// SUITS assigned by temperament group
const SUITS = {
  INTJ:'♠', INTP:'♠', ENTJ:'♠', ENTP:'♠',   // Analysts → spades (mind)
  INFJ:'♥', INFP:'♥', ENFJ:'♥', ENFP:'♥',   // Diplomats → hearts (soul)
  ISTJ:'♣', ISFJ:'♣', ESTJ:'♣', ESFJ:'♣',   // Sentinels → clubs (duty)
  ISTP:'♦', ISFP:'♦', ESTP:'♦', ESFP:'♦',   // Explorers → diamonds (action)
};

// Card rank — rarity-based
const RANKS = {
  INTJ:'A', INFJ:'A',               // Ultra Rare → Ace
  INTP:'K', ENTJ:'K',               // Rare → King
  ENTP:'Q', INFP:'Q', ENFJ:'Q',    // Uncommon → Queen
  ENFP:'J', ISTP:'J', ISFP:'J', ESTP:'J', ESFJ:'J', // Uncommon/Common → Jack
  ISTJ:'10', ISFJ:'10', ESTJ:'10', ESFP:'10',        // Common → 10
};

// Suit colors — black suits stay black, red suits use orange
const SUIT_COLOR = { '♠':'#0D0D0D', '♥':'#E85D04', '♣':'#0D0D0D', '♦':'#E85D04' };

export default function Coin({ type, coin, size = 200 }) {
  const w = size;
  const h = size * 1.4;  // card ratio
  const suit = SUITS[type] || '♠';
  const rank = RANKS[type] || '10';
  const sc   = SUIT_COLOR[suit];
  const r    = Math.min(w, h) * 0.06; // corner radius

  const fs  = w * 0.22;   // rank font size
  const sfs = w * 0.18;   // suit font size
  const cfs = w * 0.38;   // center suit font size
  const tfs = w * 0.12;   // type label font size

  return (
    <svg
      width={w} height={h}
      viewBox={`0 0 ${w} ${h}`}
      style={{ flexShrink: 0, overflow: 'visible' }}
    >
      <defs>
        <filter id={`shadow-${type}`} x="-20%" y="-10%" width="140%" height="130%">
          <feDropShadow dx="0" dy="3" stdDeviation="4" floodColor="#0D0D0D" floodOpacity="0.18" />
        </filter>
        <clipPath id={`card-clip-${type}`}>
          <rect x="0" y="0" width={w} height={h} rx={r} ry={r} />
        </clipPath>
      </defs>

      {/* Card body */}
      <rect x="0" y="0" width={w} height={h} rx={r} ry={r}
        fill="#FFFDF8" stroke="#0D0D0D" strokeWidth={w * 0.018}
        filter={`url(#shadow-${type})`}
      />

      {/* Top stripe — black */}
      <rect x="0" y="0" width={w} height={h * 0.08} rx={r} ry={r}
        fill={sc === '#0D0D0D' ? '#0D0D0D' : '#E85D04'}
        clipPath={`url(#card-clip-${type})`}
      />
      <rect x="0" y={h * 0.06} width={w} height={h * 0.02}
        fill={sc === '#0D0D0D' ? '#0D0D0D' : '#E85D04'}
      />

      {/* Bottom stripe — mirrored */}
      <rect x="0" y={h * 0.92} width={w} height={h * 0.08} rx={r} ry={r}
        fill={sc === '#0D0D0D' ? '#0D0D0D' : '#E85D04'}
        clipPath={`url(#card-clip-${type})`}
      />
      <rect x="0" y={h * 0.92} width={w} height={h * 0.02}
        fill={sc === '#0D0D0D' ? '#0D0D0D' : '#E85D04'}
      />

      {/* Top-left rank */}
      <text x={w * 0.1} y={h * 0.075} fontSize={fs} fontWeight="900"
        fill="#FFFDF8" fontFamily="'Cinzel', Georgia, serif" textAnchor="middle"
      >{rank}</text>

      {/* Top-left suit */}
      <text x={w * 0.1} y={h * 0.145} fontSize={sfs * 0.85}
        fill="#FFFDF8" fontFamily="Georgia, serif" textAnchor="middle"
      >{suit}</text>

      {/* Bottom-right rank (rotated) */}
      <text x={w * 0.9} y={h * 0.955} fontSize={fs} fontWeight="900"
        fill="#FFFDF8" fontFamily="'Cinzel', Georgia, serif" textAnchor="middle"
        transform={`rotate(180, ${w * 0.9}, ${h * 0.94})`}
      >{rank}</text>

      {/* Center suit — large */}
      <text x={w * 0.5} y={h * 0.56} fontSize={cfs}
        fill={sc} fontFamily="Georgia, serif" textAnchor="middle"
        opacity="0.12"
      >{suit}</text>

      {/* Type code — center bold */}
      <text x={w * 0.5} y={h * 0.5} fontSize={tfs * 1.05}
        fontWeight="900" fill={sc} fontFamily="'Cinzel', Georgia, serif"
        textAnchor="middle" letterSpacing={w * 0.012}
      >{type}</text>

      {/* Name below type */}
      <text x={w * 0.5} y={h * 0.585} fontSize={tfs * 0.62}
        fontWeight="600" fill={sc === '#0D0D0D' ? '#3D3D3D' : '#C14B02'}
        fontFamily="'Cinzel', Georgia, serif" textAnchor="middle"
      >{coin.metal}</text>

      {/* Motto — small italic center */}
      <text x={w * 0.5} y={h * 0.645} fontSize={tfs * 0.48}
        fill="#6B6B6B" fontFamily="'Cinzel', serif" textAnchor="middle"
        fontStyle="italic"
      >{coin.motto}</text>

      {/* Rarity badge */}
      <rect
        x={w * 0.15} y={h * 0.68} width={w * 0.7} height={h * 0.055}
        rx={h * 0.01} fill={sc === '#0D0D0D' ? '#0D0D0D' : '#E85D04'}
      />
      <text x={w * 0.5} y={h * 0.715} fontSize={tfs * 0.52}
        fontWeight="700" fill="#FFFDF8" fontFamily="Inter, sans-serif"
        textAnchor="middle" letterSpacing={w * 0.006}
      >{coin.rarity.toUpperCase()}</text>

      {/* Corner decorative suit marks */}
      <text x={w * 0.82} y={h * 0.16} fontSize={sfs * 0.7}
        fill={sc} fontFamily="Georgia, serif" textAnchor="middle" opacity="0.3"
      >{suit}</text>
      <text x={w * 0.18} y={h * 0.88} fontSize={sfs * 0.7}
        fill={sc} fontFamily="Georgia, serif" textAnchor="middle" opacity="0.3"
        transform={`rotate(180, ${w * 0.18}, ${h * 0.87})`}
      >{suit}</text>
    </svg>
  );
}
